/**
 * OMF Geolocation & Delivery Calculator
 * Handles postcode lookup, nearest store finding, and shipping tier calculation.
 * Shared by the standard and dropship paths of <postcode-checker>.
 *
 * Dependencies:
 * - window.omfPostcodeUrl — URL to postcode CDN JSON (set in snippets/js-variables.liquid)
 * - window.omfStorefrontToken — Shopify Storefront API token (set in snippets/js-variables.liquid)
 */

if (!window.omfGeo) {
const POSTCODE_CACHE_KEY = 'omfPostcodeData';
const STORE_LOCATIONS_CACHE_KEY = 'omfStoreLocations';
const STORE_LOCATIONS_CACHE_DATE_KEY = 'omfStoreLocationsCacheDate';
const VENDOR_EXCLUSIONS_CACHE_KEY = 'omfVendorExclusions';
const VENDOR_EXCLUSIONS_CACHE_DATE_KEY = 'omfVendorExclusionsCacheDate';
const NEAREST_STORE_KEY = 'omfNearestStore';
const USER_LOCATION_KEY = 'omfUserLocation';

// Postcodes that are always undeliverable — island-only locations with no road access.
// Extend this list when new hard exclusions are confirmed.
const MASTER_DELIVERY_EXCLUSIONS = new Set([
  '2898', // Lord Howe Island (NSW)
  '2899', // Norfolk Island (NSW)
  '4803', // Hamilton Island (QLD)
  '4875', // Thursday Island / Torres Strait (QLD)
]);

const SHIPPING_TIERS = [
  { max: 15, rate: 99, label: 'Delivery to Room' },
  { max: 30, rate: 120, label: 'Standard Shipping' },
  { max: 50, rate: 140, label: 'Standard Shipping' },
  { max: 80, rate: 180, label: 'Standard Shipping' },
  { max: 150, rate: 250, label: 'Standard Shipping' },
  { max: 300, rate: 370, label: 'Standard Shipping' },
  { max: Infinity, rate: 370, label: 'Please call your nearest store to confirm' },
];

// BiB (Bed in a Box) — mirrors JSRates bib-delivery.js: variants tagged
// custom.rate_category = "BiB" pay $29 per piece in Tier 1 (<= 15km) when the
// nearest store is on this metro list. Anything else keeps the SHIPPING_TIERS fee.
// Store names are matched loosely (lowercase, leading "OMF " and non-alphanumerics
// stripped) against the store_name on the store metaobject.
const BIB_CATEGORY = 'bib';
const BIB_FEE_PER_PIECE = 29;
const BIB_MAX_DISTANCE_KM = 15;
const BIB_LABEL = 'Standard Delivery';
const BIB_DESCRIPTION = 'Our team will be in touch to arrange delivery';
const BIB_METRO_STORES = new Set([
  'morayfield', 'northlakes', 'capalaba', 'macgregor', 'logan', 'ipswich', 'jindalee',
  'artarmon', 'castlehill', 'prospect', 'penrith', 'auburn', 'alexandria',
  'casula', 'crossroads', 'campbelltown',
  'moorabbin', 'dandenong', 'cranbourne',
]);

function normalizeStoreName(name) {
  return String(name ?? '').toLowerCase().replace(/^omf\s+/, '').replace(/[^a-z0-9]/g, '');
}

// Returns { rate, label, description } when the BiB rate applies, otherwise null
function getBibDelivery(store, distanceKm, rateCategory) {
  if (String(rateCategory ?? '').trim().toLowerCase() !== BIB_CATEGORY) return null;
  if (!(distanceKm <= BIB_MAX_DISTANCE_KM)) return null;
  if (!store || !BIB_METRO_STORES.has(normalizeStoreName(store.name))) return null;
  return { rate: BIB_FEE_PER_PIECE, label: BIB_LABEL, description: BIB_DESCRIPTION };
}

// ================================================
// POSTCODE LOOKUP
// ================================================

function isValidCoordinate(entry) {
  return !!entry && !isNaN(parseFloat(entry.Longitude)) && parseFloat(entry.Longitude) > 0;
}

// Some rows in the source postcode file have Longitude wrongly duplicated from
// Latitude — a data export bug affecting ~700 rows (mostly PO boxes, DCs, and
// shopping-centre-only postcodes), spread across every state. Repair them in
// place by borrowing coordinates from a nearby postcode — a sibling entry
// sharing the same postcode first, then walking outward numerically (same
// state preferred) — so no consumer of this data needs to special-case bad
// rows or fall over with a wildly wrong distance.
function repairPostcodeCoordinates(data) {
  const byPostcode = new Map();
  data.forEach((entry) => {
    if (isValidCoordinate(entry)) {
      const list = byPostcode.get(entry.Postocode) || [];
      list.push(entry);
      byPostcode.set(entry.Postocode, list);
    }
  });

  data.forEach((entry) => {
    if (isValidCoordinate(entry)) return;

    const siblings = byPostcode.get(entry.Postocode);
    if (siblings && siblings.length) {
      entry.Latitude = siblings[0].Latitude;
      entry.Longitude = siblings[0].Longitude;
      return;
    }

    const pc = parseInt(entry.Postocode, 10);
    if (isNaN(pc)) return;

    for (let offset = 1; offset <= 50; offset++) {
      const candidatePcs = [pc - offset, pc + offset].map((n) => n.toString().padStart(4, '0'));
      for (const candidatePc of candidatePcs) {
        const candidates = byPostcode.get(candidatePc);
        if (candidates) {
          const best = candidates.find((c) => c.State === entry.State) || candidates[0];
          entry.Latitude = best.Latitude;
          entry.Longitude = best.Longitude;
          return;
        }
      }
    }
  });

  return data;
}

async function getPostcodeData() {
  const cached = sessionStorage.getItem(POSTCODE_CACHE_KEY);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (e) {
      sessionStorage.removeItem(POSTCODE_CACHE_KEY);
    }
  }

  const url = window.omfPostcodeUrl;
  if (!url) {
    console.error('OMF: window.omfPostcodeUrl is not defined');
    return [];
  }

  try {
    const response = await fetch(url);
    const data = repairPostcodeCoordinates(await response.json());
    sessionStorage.setItem(POSTCODE_CACHE_KEY, JSON.stringify(data));
    return data;
  } catch (e) {
    console.error('OMF: Failed to fetch postcode data', e);
    return [];
  }
}

async function getPostcodeMatches(postcode) {
  const data = await getPostcodeData();
  if (!data.length) return [];
  return data.filter((entry) => entry.Postocode === postcode.toString());
}

async function isValidPostcode(postcode) {
  const matches = await getPostcodeMatches(postcode);
  return matches.length > 0;
}

async function getPostcodeLatLng(postcode) {
  const matches = await getPostcodeMatches(postcode);
  if (!matches.length) return null;

  // Skip entries with invalid longitude (must be positive for Australia)
  // PO Box postcodes often have bad coordinate data
  const valid = matches.find((m) => parseFloat(m.Longitude) > 0);
  if (!valid) return null;

  return {
    lat: parseFloat(valid.Latitude),
    lng: parseFloat(valid.Longitude),
  };
}

// ================================================
// STORE LOCATIONS — Storefront API
// ================================================

/**
 * Fetch all store locations from Shopify Storefront API
 * Cached in sessionStorage daily so it only fetches once per day
 * @returns {Promise<Array>} Array of parsed store objects
 */
async function fetchStoreLocations() {
  // Check cache — only re-fetch once per day
  const cachedData = sessionStorage.getItem(STORE_LOCATIONS_CACHE_KEY);
  const cacheDate = sessionStorage.getItem(STORE_LOCATIONS_CACHE_DATE_KEY);
  const today = new Date().toDateString();

  if (cachedData && cacheDate === today) {
    try {
      return JSON.parse(cachedData);
    } catch (e) {
      sessionStorage.removeItem(STORE_LOCATIONS_CACHE_KEY);
      sessionStorage.removeItem(STORE_LOCATIONS_CACHE_DATE_KEY);
    }
  }

  const token = window.omfStorefrontToken;
  if (!token) {
    console.error('OMF: window.omfStorefrontToken is not defined');
    return [];
  }

  const query = `
    {
      metaobjects(type: "locations", first: 250) {
        edges {
          node {
            handle
            store_name: field(key: "store_name") { value }
            store_title: field(key: "store_title") { value }
            store_location: field(key: "store_location") { value }
            store_address: field(key: "store_address") { value }
            store_city: field(key: "store_city") { value }
            store_postcode: field(key: "store_postcode") { value }
            store_tel: field(key: "store_tel") { value }
            store_email: field(key: "store_email") { value }
            store_page_url: field(key: "store_page_url") { value }
            store_link: field(key: "store_link") { value }
            store_map_link: field(key: "store_map_link") { value }
            store_place_id: field(key: "store_place_id") { value }
            store_temp_title: field(key: "store_temp_title") { value }
            store_times_title: field(key: "store_times_title") { value }
            store_monday: field(key: "store_monday") { value }
            store_tuesday: field(key: "store_tuesday") { value }
            store_wednesday: field(key: "store_wednesday") { value }
            store_thursday: field(key: "store_thursday") { value }
            store_friday: field(key: "store_friday") { value }
            store_saturday: field(key: "store_saturday") { value }
            store_sunday: field(key: "store_sunday") { value }
            enable_store: field(key: "enable_store") { value }
            warehouse: field(key: "warehouse") { value }
            nearest_dc: field(key: "nearest_dc") { value }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${window.Shopify.shop}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status}`);
    }

    const json = await response.json();
    const edges = json.data?.metaobjects?.edges ?? [];

    const stores = edges
      .map(({ node }) => ({
        handle: node.handle,
        name: node.store_name?.value ?? '',
        title: node.store_title?.value ?? '',
        location: node.store_location?.value ?? '',
        address: node.store_address?.value ?? '',
        city: node.store_city?.value ?? '',
        postcode: node.store_postcode?.value ?? '',
        phone: node.store_tel?.value ?? '',
        email: node.store_email?.value ?? '',
        pageUrl: node.store_page_url?.value ?? '',
        mapsLink: node.store_link?.value ?? '',
        mapEmbed: node.store_map_link?.value ?? '',
        placeId: node.store_place_id?.value ?? '',
        tempTitle: node.store_temp_title?.value ?? '',
        timesTitle: node.store_times_title?.value ?? '',
        hours: {
          monday: node.store_monday?.value ?? '',
          tuesday: node.store_tuesday?.value ?? '',
          wednesday: node.store_wednesday?.value ?? '',
          thursday: node.store_thursday?.value ?? '',
          friday: node.store_friday?.value ?? '',
          saturday: node.store_saturday?.value ?? '',
          sunday: node.store_sunday?.value ?? '',
        },
        enabled: node.enable_store?.value === 'true',
        warehouse: node.warehouse?.value === 'true',
        dcRegion: node.nearest_dc?.value ?? '',
      }))
      .filter((store) => store.name && store.location);

    sessionStorage.setItem(STORE_LOCATIONS_CACHE_KEY, JSON.stringify(stores));
    sessionStorage.setItem(STORE_LOCATIONS_CACHE_DATE_KEY, today);

    return stores;
  } catch (e) {
    console.error('OMF: Failed to fetch store locations', e);
    return [];
  }
}

// ================================================
// STORE FILTERING & PARSING
// ================================================

/**
 * Parse the store location field — auto-detects lat/lng order
 * Australian latitude is always negative (-10 to -45)
 * Australian longitude is always positive (113 to 154)
 */
function parseStoreLocation(locationString) {
  if (!locationString) return null;
  const parts = locationString.split(',').map((s) => parseFloat(s.trim()));
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;

  let lat, lng;
  if (parts[0] < 0 && parts[1] > 0) {
    lat = parts[0];
    lng = parts[1];
  } else {
    lng = parts[0];
    lat = parts[1];
  }

  return { lat, lng };
}

/**
 * Get all active, non-warehouse stores with parsed coordinates
 * @returns {Promise<Array>}
 */
async function getFulfillableStores() {
  const stores = await fetchStoreLocations();

  return stores
    .filter((store) => store.enabled === true && store.warehouse !== true)
    .map((store) => {
      const coords = parseStoreLocation(store.location);
      return {
        ...store,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      };
    })
    .filter((store) => store.lat !== null && store.lng !== null);
}

/**
 * Get all active, non-warehouse stores with parsed coordinates and full
 * detail fields (hours, contact info, links) — used by the store locator
 * and store detail pages, as opposed to getFulfillableStores()'s narrower
 * shape for the PDP delivery calculator.
 * @returns {Promise<Array>}
 */
async function getAllStores() {
  const stores = await fetchStoreLocations();

  return stores
    .filter((store) => store.enabled === true && store.warehouse !== true)
    .map((store) => {
      const coords = parseStoreLocation(store.location);
      return {
        ...store,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      };
    });
}

// ================================================
// VENDOR POSTCODE EXCLUSIONS
// ================================================

async function fetchVendorExclusions() {
  const cachedData = sessionStorage.getItem(VENDOR_EXCLUSIONS_CACHE_KEY);
  const cacheDate = sessionStorage.getItem(VENDOR_EXCLUSIONS_CACHE_DATE_KEY);
  const today = new Date().toDateString();

  if (cachedData && cacheDate === today) {
    try {
      return JSON.parse(cachedData);
    } catch (e) {
      sessionStorage.removeItem(VENDOR_EXCLUSIONS_CACHE_KEY);
      sessionStorage.removeItem(VENDOR_EXCLUSIONS_CACHE_DATE_KEY);
    }
  }

  const token = window.omfStorefrontToken;
  if (!token) {
    console.error('OMF: window.omfStorefrontToken is not defined');
    return [];
  }

  const query = `
    {
      metaobjects(type: "vendor_postcode_exclusions", first: 250) {
        edges {
          node {
            vendor: field(key: "vendor") { value }
            excluded_postcodes: field(key: "excluded_postcodes") { value }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${window.Shopify.shop}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) throw new Error(`Storefront API error: ${response.status}`);

    const json = await response.json();
    const edges = json.data?.metaobjects?.edges ?? [];

    const exclusions = edges
      .map(({ node }) => ({
        vendor: node.vendor?.value ?? '',
        excludedPostcodes: (node.excluded_postcodes?.value ?? '')
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
      }))
      .filter((e) => e.vendor);

    sessionStorage.setItem(VENDOR_EXCLUSIONS_CACHE_KEY, JSON.stringify(exclusions));
    sessionStorage.setItem(VENDOR_EXCLUSIONS_CACHE_DATE_KEY, today);

    return exclusions;
  } catch (e) {
    console.error('OMF: Failed to fetch vendor exclusions', e);
    return [];
  }
}

async function getVendorExclusions(vendorName) {
  const exclusions = await fetchVendorExclusions();
  const match = exclusions.find((e) => e.vendor === vendorName);
  const vendorPostcodes = match?.excludedPostcodes ?? [];
  return [...new Set([...MASTER_DELIVERY_EXCLUSIONS, ...vendorPostcodes])];
}

// ================================================
// DISTANCE CALCULATION
// ================================================

function calculateDistance(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ================================================
// NEAREST STORE
// ================================================

/**
 * Find the nearest store to a given lat/lng
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{store: Object, distance: number}|null>}
 */
async function getNearestStore(lat, lng) {
  const stores = await getFulfillableStores();
  if (!stores.length) return null;

  let nearest = null;
  let minDistance = Infinity;

  for (const store of stores) {
    const distance = calculateDistance(lat, lng, store.lat, store.lng);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = store;
    }
  }

  if (!nearest) return null;

  return {
    store: nearest,
    distance: parseFloat(minDistance.toFixed(2)),
  };
}

// ================================================
// SHIPPING TIER
// ================================================

function getShippingTier(distanceKm) {
  for (const tier of SHIPPING_TIERS) {
    if (distanceKm <= tier.max) {
      return { rate: tier.rate, label: tier.label };
    }
  }
  return { rate: 370, label: 'Please call your nearest store to confirm' };
}

// ================================================
// MAIN — CHECK AVAILABILITY
// ================================================

async function checkAvailability(postcode) {
  const postcodeStr = postcode.toString().trim();
  if (!/^\d{4}$/.test(postcodeStr)) {
    return { valid: false, error: 'Please enter a valid 4-digit postcode' };
  }

  const valid = await isValidPostcode(postcodeStr);
  if (!valid) {
    return { valid: false, error: 'Postcode not found. Please check and try again.' };
  }

  const coords = await getPostcodeLatLng(postcodeStr);
  if (!coords) {
    return { valid: false, error: 'Could not locate postcode. Please try again.' };
  }

  const result = await getNearestStore(coords.lat, coords.lng);
  if (!result) {
    return { valid: false, error: 'No stores found. Please contact us for assistance.' };
  }

  const shipping = getShippingTier(result.distance);

  sessionStorage.setItem(NEAREST_STORE_KEY, JSON.stringify(result.store));
  sessionStorage.setItem(USER_LOCATION_KEY, JSON.stringify({ postcode: postcodeStr, ...coords }));

  return {
    valid: true,
    nearestStore: result.store,
    distance: result.distance,
    shipping,
  };
}

// ================================================
// EXPORTS
// ================================================
window.omfGeo = {
  checkAvailability,
  getPostcodeData,
  getPostcodeMatches,
  getNearestStore,
  getShippingTier,
  getBibDelivery,
  calculateDistance,
  getFulfillableStores,
  getAllStores,
  getVendorExclusions,
};
}
